import React, { FC, useContext, useEffect, useState } from 'react';
import { WTooltip, WTypography } from '../../wrappers/wrapper';
import styles from './notifications-content.module.scss';
import { useNavigate } from 'react-router-dom';
import { DoneAll, SettingsOutlined } from '@mui/icons-material';
import NotificationTab from '../../organisms/notification-tab/notification-tab';
import { IPaginate, IQueryParams, TArticle, TNotification } from '../../../models/common';
import dbService from '../../../services/database.service';
import Button from 'components/atoms/button/button';
import CloseIcon from '@mui/icons-material/Close';
import useDeviceType from 'custom-hooks/useDeviceType';
import { UserContext } from 'contexts/userContext';

enum Tabs {
  INBOX = 'Inbox',
  ARCHIVED = 'Archived',
  ALL = 'All',
}

interface NotificationsContentProps {
  close: (params?: { userId?: string; profileTab?: number }) => void;
}
const FETCH_LIMIT = 12;
let lastFetchTime = Date.now();
const NotificationsContent: FC<NotificationsContentProps> = ({ close }) => {
  const { isDesktop } = useDeviceType();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const defaultQueryParams: IQueryParams = {
    filter: {
      receiver: user?._id,
      archived: false,
    },
    relation: ['article', 'comment', 'sender'],
    sort: { _id: -1 },
    limit: FETCH_LIMIT,
    paginate: true,
  };

  const [loading, setLoading] = useState(true);
  const [indexOfTab, setIndexOfTab] = useState(0);
  const [notifications, setNotifications] = useState<TNotification[]>([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [queryParams, setQueryParams] = useState<IQueryParams>(defaultQueryParams);

  useEffect(() => {
    fetchNotifications();
  }, [queryParams]);

  useEffect(() => {
    setLoading(true);
    setNotifications([]);
    setSkip(0);
    setHasMore(true);
    setQueryParams({
      ...defaultQueryParams,
      filter: {
        receiver: user?._id,
        ...getFilter(indexOfTab),
      },
    });
  }, [indexOfTab]);

  const fetchNotifications = async () => {
    if (!canFetch()) return;

    const res = await dbService.getNotifications({ ...queryParams, skip });

    const resPaginate = res as unknown as IPaginate<TNotification>;

    const notificationsArr = [...notifications, ...resPaginate.data];
    setSkip((prevSkip) => prevSkip + FETCH_LIMIT);
    setHasMore(notificationsArr.length < resPaginate.meta.total);
    setNotifications(notificationsArr);
    setLoading(false);
  };

  const handleClickNotification = (notification: TNotification) => {
    dbService.updateNotification(notification._id!, { is_read: true });
    navigate('a/' + (notification.article as TArticle)._id);
    close();
  };

  const setAllRead = async () => {
    if (!notifications?.length) return;

    const promises: Promise<TNotification>[] = [];

    notifications.forEach((el) => {
      promises.push(dbService.updateNotification(el._id!, { is_read: true }));
    });

    const res = await Promise.all(promises).catch(console.error);
    if (!res) return;

    setNotifications((notifications) => notifications?.map((item) => ({ ...item, is_read: true })));
  };

  const getFilter = (index: number) => {
    const filters = [{ archived: false }, { archived: true }];
    return filters[index] || {};
  };

  const canFetch = () => {
    if (Date.now() - lastFetchTime > 20) {
      lastFetchTime = Date.now();
      return true;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <div className={styles.header}>
          <WTypography className={styles.title}>Notifications</WTypography>
          <div className={styles.actions}>
            <WTooltip title={'Mark all as read'} onClick={setAllRead}>
              <DoneAll />
            </WTooltip>
            <WTooltip
              title={'Notification Settings'}
              onClick={() => {
                close({ profileTab: 1 });
              }}
            >
              <SettingsOutlined />
            </WTooltip>
            {!isDesktop && (
              <Button type="base" onClick={() => close()}>
                <CloseIcon fontSize="small" />
              </Button>
            )}
          </div>
        </div>
        <div className={styles.tabs}>
          {Object.values(Tabs).map((tab, index) => (
            <WTypography
              key={index}
              className={`${styles.tab} ${index === indexOfTab && styles.active}`}
              onClick={() => setIndexOfTab(index)}
            >
              {tab}
            </WTypography>
          ))}
        </div>
      </div>
      <NotificationTab
        loading={loading}
        notifications={notifications}
        onClickProfile={(id) => {
          close({ userId: id });
        }}
        onClick={handleClickNotification}
        hasMore={hasMore}
        fetchData={fetchNotifications}
      />
    </div>
  );
};

export default NotificationsContent;
